import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/Logo";
import { CheckCircle2, XCircle, ArrowRight, ArrowLeft, Trophy, RotateCcw } from "lucide-react";

export default function Quiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setResult(null); setAnswers({}); setIdx(0);
    api.get(`/quizzes/${quizId}/start`).then((r) => setQuiz(r.data.quiz)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [quizId]);

  if (!quiz) return <Spinner label="Preparing your quiz..." />;

  const q = quiz.questions[idx];
  const multi = q?.type === "multi";

  const toggle = (optId) => {
    setAnswers((prev) => {
      const cur = prev[q.id] || [];
      if (multi) {
        return { ...prev, [q.id]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
      }
      return { ...prev, [q.id]: [optId] };
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${quizId}/submit`, { answers });
      setResult(data);
      await refresh();
      if (data.passed) toast.success(`Passed! Score ${data.score}%${data.xp_awarded ? ` · +${data.xp_awarded} XP` : ""}`);
      else toast.error(`Score ${data.score}%. You need ${quiz.pass_threshold}% to pass.`);
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="quiz-result">
        <div className={`card p-8 text-center ${result.passed ? "border-emerald-500/30" : "border-red-500/30"}`}>
          {result.passed ? <Trophy className="mx-auto text-[#F26522]" size={48} /> : <RotateCcw className="mx-auto text-slate-400" size={48} />}
          <h1 className="font-heading text-3xl font-bold mt-4">{result.passed ? "Well done!" : "Almost there"}</h1>
          <div className="font-heading text-5xl font-extrabold mt-4" style={{ color: result.passed ? "#10B981" : "#EF4444" }}>{result.score}%</div>
          <p className="text-slate-400 mt-2">{result.correct} of {result.total} correct{result.xp_awarded ? ` · +${result.xp_awarded} XP earned` : ""}</p>
          <div className="flex items-center justify-center gap-3 mt-6">
            {!result.passed && <button className="btn-secondary" onClick={load} data-testid="retry-quiz-btn"><RotateCcw size={16} /> Retry</button>}
            <button className="btn-primary" onClick={() => navigate(-1)} data-testid="quiz-back-btn">Back to module <ArrowRight size={16} /></button>
          </div>
        </div>

        <h2 className="font-heading text-lg font-semibold mt-8 mb-3">Review</h2>
        <div className="space-y-3">
          {quiz.questions.map((question, i) => {
            const rev = result.review.find((x) => x.question_id === question.id);
            return (
              <div key={question.id} className="card p-5" data-testid={`review-${i}`}>
                <div className="flex items-start gap-3">
                  {rev?.correct ? <CheckCircle2 className="text-emerald-400 mt-0.5 shrink-0" size={18} /> : <XCircle className="text-red-400 mt-0.5 shrink-0" size={18} />}
                  <div className="flex-1">
                    <p className="font-medium">{question.text}</p>
                    <div className="mt-2 space-y-1.5">
                      {question.options.map((o) => {
                        const isCorrect = rev?.correct_answer_ids?.includes(String(o.id));
                        const chosen = (answers[question.id] || []).includes(o.id);
                        return (
                          <div key={o.id} className={`text-sm px-3 py-1.5 rounded-lg border ${isCorrect ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : chosen ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-white/5 text-slate-400"}`}>
                            {o.text}
                          </div>
                        );
                      })}
                    </div>
                    {rev?.explanation && <p className="text-xs text-slate-400 mt-2 italic">{rev.explanation}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const selected = answers[q.id] || [];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="max-w-3xl mx-auto" data-testid="quiz-page">
      <button className="btn-ghost text-sm mb-4 -ml-2" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Exit quiz</button>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading text-xl font-bold">{quiz.title}</h1>
        <span className="text-sm text-slate-400">Question {idx + 1} / {quiz.total_questions}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 mb-8 overflow-hidden">
        <div className="h-full bg-[#F26522] transition-all" style={{ width: `${((idx + 1) / quiz.total_questions) * 100}%` }} />
      </div>

      <div className="card p-6 sm:p-8">
        <p className="font-heading text-lg font-medium">{q.text}</p>
        {multi && <p className="text-xs text-slate-400 mt-1">Select all that apply</p>}
        <div className="mt-6 space-y-3">
          {q.options.map((o) => {
            const active = selected.includes(o.id);
            return (
              <button
                key={o.id}
                onClick={() => toggle(o.id)}
                data-testid={`quiz-option-${o.id}`}
                className={`w-full text-left px-4 py-3.5 rounded-lg border transition-all flex items-center gap-3 ${active ? "border-[#F26522] bg-[#F26522]/10 text-white" : "border-white/10 text-slate-300 hover:border-white/25 hover:bg-white/5"}`}
              >
                <span className={`h-5 w-5 rounded${multi ? "" : "-full"} border flex items-center justify-center shrink-0 ${active ? "border-[#F26522] bg-[#F26522]" : "border-white/30"}`}>
                  {active && <CheckCircle2 size={14} className="text-white" />}
                </span>
                {o.text}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button className="btn-ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)}><ArrowLeft size={16} /> Previous</button>
        {idx < quiz.questions.length - 1 ? (
          <button className="btn-secondary" disabled={selected.length === 0} onClick={() => setIdx(idx + 1)} data-testid="quiz-next-btn">Next <ArrowRight size={16} /></button>
        ) : (
          <button className="btn-primary" disabled={answeredCount < quiz.total_questions || submitting} onClick={submit} data-testid="quiz-submit-btn">
            Submit quiz
          </button>
        )}
      </div>
    </div>
  );
}
